import { useEffect, useRef } from "react";
import { useDebouncedFunction } from "../../../../../../../hooks/useDebouncedFunction.ts";
import { useAppQuery } from "../../../../../../../http/useAppQuery.ts";
import { hexToRgbaString } from "../../../../../../../utils/colors.ts";
import { COPY_AD_GENERATION_TYPE } from "../../../../../constants.ts";
import { useSelectedAsset } from "../../../../../hooks/useSelectedAsset.ts";
import type { CopyAdPipelineData, PipelineData } from "../../../../../types.ts";
import { ASSET_LAYER_TYPE } from "../../../Designer/constants.ts";
import { useLayers } from "../../../Designer/hooks/useLayers.ts";
import { designerCanvasStore } from "../../../Designer/stores/designerCanvasStore.ts";

export type AdCompositionBuildStatus =
  | "pending"
  | "loading"
  | "success"
  | "error";

export const useAdCompositionBuilder = () => {
  const { selectedAsset } = useSelectedAsset();
  const nodeUuid = selectedAsset?.content.node_uuid;
  const status = useRef<AdCompositionBuildStatus>("pending");

  const { data: nodeData } = useAppQuery<PipelineData>({
    queryKey: nodeUuid ? `nodes/${nodeUuid}` : null,
    enabled: !!nodeUuid,
  });

  // FIXME: We debounce the build composition to avoid building the composition several times on component re-render.
  // This is a temporary issue which will be fixed when the backend will be able to build the composition itself.
  const debouncedBuildComposition = useDebouncedFunction(
    (pipelineData: CopyAdPipelineData) => {
      if (!fabricCanvas) return;
      if (!pipelineData.layout) return;
      for (const paragraph of pipelineData.layout.paragraphs) {
        for (const textBox of paragraph.text_boxes) {
          designerCanvasStore.addTextLayer({
            content: textBox.text,
            left: textBox.bbox.x + textBox.bbox.w * 0.5,
            top: textBox.bbox.y + textBox.bbox.h * 0.5,
            width: textBox.bbox.w,
            fontSize: Math.floor(textBox.bbox.h),
            textColor: textBox.color
              ? hexToRgbaString(textBox.color)
              : "rgba(255, 255, 255, 1)",
            fontFamily: textBox.font_family ?? undefined,
            textAlign: paragraph.alignment,
          });
        }
      }
      fabricCanvas.renderAll();
    },
    50,
  );

  const fabricCanvas = designerCanvasStore.useState((it) => it.fabricCanvas);
  const layers = useLayers();

  useEffect(() => {
    if (!nodeData) return;
    if (nodeData.pipeline_data.data.type !== COPY_AD_GENERATION_TYPE) return;
    if (
      fabricCanvas &&
      layers.some((it) => it.layerType !== ASSET_LAYER_TYPE)
    ) {
      return;
    }
    debouncedBuildComposition(nodeData.pipeline_data.data);
  }, [nodeData, fabricCanvas, layers, debouncedBuildComposition]);

  if (nodeData?.pipeline_data.data.type !== COPY_AD_GENERATION_TYPE) {
    status.current = "pending";
  } else if (nodeData.pipeline_data.status === "RUNNING") {
    status.current = "loading";
  } else if (nodeData.pipeline_data.status === "SUCCEEDED") {
    status.current = "success";
  } else if (nodeData.pipeline_data.status === "FAILED") {
    status.current = "error";
  }

  return {
    status: status.current,
  };
};
